import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define theme colors as constants at the top
const THEME_COLORS = {
  primary: {
    main: '#945060',
    light: '#ac6c7c',
    dark: '#7d3e4e',
  },
  secondary: {
    main: '#776963',
    light: '#908580',
    dark: '#5e514c',
  },
  accent: '#d16d5e',
  success: '#8fb9aa',
  warning: '#d16d5e',
  danger: '#b24a3c',
  critical: '#b24a3c',
  info: '#8fb9aa',
  background: {
    default: '#ffffff',
    alt: '#faf7f2',
  },
  section: {
    light: '#faf7f2',
    dark: '#f0ebe6',
  },
  text: {
    primary: '#4a4a4a',
    secondary: '#776963',
    light: '#ffffff',
  },
  border: '#e0e0e0',
  score: {
    none: '#8fb9aa',
    low: '#945060',
    moderate: '#d16d5e',
    high: '#c85c4d',
    critical: '#b24a3c',
  }
};

// Define the theme context type
interface ThemeContextType {
  colors: typeof THEME_COLORS;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

// Create theme context
const ThemeContext = createContext<ThemeContextType>({
  colors: THEME_COLORS,
  isDarkMode: false,
  toggleDarkMode: () => {},
});

// Custom hook to use theme
export const useTheme = () => useContext(ThemeContext);

// Theme provider props interface
interface ThemeProviderProps {
  children: ReactNode;
}

// Theme provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);

  // Function to toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  // Adjust colors based on dark mode
  const colors = isDarkMode
    ? {
        ...THEME_COLORS,
        background: {
          default: '#1a1a1a',
          alt: '#2a2a2a',
        },
        section: {
          light: '#2a2a2a',
          dark: '#333333',
        },
        text: {
          primary: '#f0f0f0',
          secondary: '#c0c0c0',
          light: '#ffffff',
        },
        border: '#444444',
      }
    : THEME_COLORS;

  return (
    <ThemeContext.Provider value={{ colors, isDarkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider; 